import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import SaveIcon from '@mui/icons-material/Save';
import Warning from '@mui/icons-material/Warning';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { grey, red } from '@mui/material/colors';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import ErrorUtil from '@/util/ErrorUtil';
import StringUtil from '@/util/StringUtil';
import ReactComponent from '../../ReactComponent';
import ModalConfirmacao, { TIPO_CONFIRMACAO } from '../ModalConfirmacao';
import DataUtil from '@/util/DataUtil';

/** @constant {number} */
const QTD_MAXIMA_CARACTERES_TRANSCRICAO = 40000;

/** @constant {number} */
const QTD_MAXIMA_CARACTERES_OBSERVACAO = 400;

/** @enum {string} */
const TIPO_ACAO = { CONSULTAR: 'Consultando', SALVAR: 'Gravando', EXCLUIR: 'Excluindo' };

/** @constant {string} */
const PERMITIR_APENAS_OBSERVACAO = false; // Para alterar para "true" faz-se necessário alterar o campo TXT_TRANSCRICAO para NULL

class TranscricaoDocumentoSustentacaoOralDialog extends ReactComponent {
    textoTranscricaoBD = '';
    textoObservacaoBD = '';
    textoTranscricao = '';
    textoObservacao = '';
    dataInclusao = '';
    cpfInclusao = '';
    localizacaoResponsavelInclusao = '';
    dataAtualizacao = '';
    cpfAtualizacao = '';
    localizacaoResponsavelAtualizacao = '';
    jaEditou = false;
    modoEdicao = false;
    openConfirmacaoExclusao = false;
    executandoAcao = TIPO_ACAO.CONSULTAR;
    erroConsultaTranscricao = '';

    constructor(props) {
        super(props);

        makeObservable(this, {
            textoTranscricaoBD: observable,
            textoObservacaoBD: observable,
            textoTranscricao: observable,
            textoObservacao: observable,
            dataInclusao: observable,
            cpfInclusao: observable,
            localizacaoResponsavelInclusao: observable,
            dataAtualizacao: observable,
            cpfAtualizacao: observable,
            localizacaoResponsavelAtualizacao: observable,
            jaEditou: observable,
            modoEdicao: observable,
            openConfirmacaoExclusao: observable,
            executandoAcao: observable,
            erroConsultaTranscricao: observable,
            arquivoId: computed,
            isTranscricaoPreenchida: computed,
            isObservacaoPreenchida: computed,
            isCamposObrigatoriosPreenchidos: computed,
            hasAlteracao: computed,
            msgErroForm: computed,
            hasError: computed,
            consultar: action,
            handleChangeTranscricao: action,
            handleChangeObservacao: action,
            handleClickSalvar: action,
            handleCloseConfirmacaoExclusao: action,
            handleClickConfirmacaoExclusao: action,
            handleClickExcluir: action,
            renderErroCarregamento: computed,
            renderInfo: computed,
            renderForm: computed
        });
    }

    componentDidMount() {
        this.consultar();
    }

    get arquivoId() {
        return this.observableProps.idDocumento;
    }

    get isTranscricaoPreenchida() {
        return !!(this.textoTranscricao && this.textoTranscricao.trim().length > 0);
    }

    get isObservacaoPreenchida() {
        return !!(this.textoObservacao && this.textoObservacao.trim().length > 0);
    }

    get isCamposObrigatoriosPreenchidos() {
        if (PERMITIR_APENAS_OBSERVACAO) {
            return !!(this.isTranscricaoPreenchida || this.isObservacaoPreenchida);
        }

        return this.isTranscricaoPreenchida;
    }

    get hasAlteracao() {
        return (
            this.textoObservacao.trim() === this.textoObservacaoBD &&
            this.textoTranscricao.trim() === this.textoTranscricaoBD
        );
    }

    get msgErroForm() {
        const apenasObs = PERMITIR_APENAS_OBSERVACAO ? 'e(ou) a observação.' : '.';

        return this.jaEditou && !this.isCamposObrigatoriosPreenchidos
            ? `Informe o texto da transcrição do arquivo de áudio/vídeo${apenasObs}`
            : '';
    }

    get hasError() {
        return this.msgErroForm !== '';
    }

    consultar = async () => {
        if (this.arquivoId) {
            this.executandoAcao = TIPO_ACAO.CONSULTAR;

            try {
                const resposta = await this.sessaoService.consultarTranscricaoSustentacaoOralEletronica(this.arquivoId);
                runInAction(() => {
                    this.modoEdicao = !!resposta?.observacao || !!resposta?.transcricao;
                    this.textoObservacao = resposta?.observacao || '';
                    this.textoTranscricao = resposta?.transcricao || '';

                    if (this.modoEdicao) {
                        this.textoObservacaoBD = this.textoObservacao;
                        this.textoTranscricaoBD = this.textoTranscricao;
                        this.dataInclusao = resposta?.dataInclusao || '';
                        this.dataAtualizacao = resposta?.dataAtualizacao || '';
                        this.cpfInclusao = resposta?.cpfInclusao || '';
                        this.cpfAtualizacao = resposta?.cpfAtualizacao || '';
                        this.localizacaoResponsavelInclusao = resposta?.siglaOjInclusao || '';
                        this.localizacaoResponsavelAtualizacao = resposta?.siglaOjAtualizacao || '';
                    }
                });
            } catch (error) {
                runInAction(() => {
                    this.erroConsultaTranscricao = ErrorUtil.getMensagemErro(error);
                });
            } finally {
                runInAction(() => {
                    this.executandoAcao = '';
                });
            }
        }
    };

    infoResponsavel(cpf, localizacao) {
        let descricao = '';

        if (cpf) {
            descricao += cpf;
        }

        if (localizacao) {
            if (cpf) {
                descricao += ` (${localizacao})`;
            } else {
                descricao = localizacao;
            }
        }

        return descricao;
    }

    handleChangeTranscricao = (e) => {
        this.jaEditou = true;
        const texto = e.target.value;
        this.textoTranscricao = StringUtil.tratarCaracteresNaoLatin1(texto);
    };

    handleChangeObservacao = (e) => {
        this.jaEditou = true;
        const texto = e.target.value;
        this.textoObservacao = StringUtil.tratarCaracteresNaoLatin1(texto);
    };

    handleClickSalvar = async () => {
        if (this.isCamposObrigatoriosPreenchidos) {
            this.textoTranscricao = this.textoTranscricao.trim();
            this.textoObservacao = this.textoObservacao.trim();

            this.executandoAcao = TIPO_ACAO.SALVAR;
            try {
                await this.observableProps.onSalvarTranscricaoDocumento(this.textoTranscricao, this.textoObservacao);
                runInAction(() => {
                    this.textoObservacaoBD = this.textoObservacao;
                    this.textoTranscricaoBD = this.textoTranscricao;
                });
                this.mensagemStore.abrirMensagemSucesso('Dados da transcrição gravados com sucesso.');
            } finally {
                runInAction(() => {
                    this.executandoAcao = '';
                });
            }
        }
    };

    handleCloseConfirmacaoExclusao = () => {
        this.openConfirmacaoExclusao = false;
    };

    handleClickConfirmacaoExclusao = () => {
        this.openConfirmacaoExclusao = true;
    };

    handleClickExcluir = async () => {
        this.executandoAcao = TIPO_ACAO.EXCLUIR;
        try {
            await this.observableProps.onExcluirTranscricaoDocumento(this.observableProps.idDocumento);
            this.mensagemStore.abrirMensagemSucesso('Dados da transcrição excluídos com sucesso.');
        } finally {
            runInAction(() => {
                this.executandoAcao = '';
                this.openConfirmacaoExclusao = false;
            });
        }
    };

    get renderCarregandoDialog() {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexGrow: 1,
                    p: 1,
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
            >
                <CircularProgress
                    disableShrink={true}
                    size={64}
                />
            </Box>
        );
    }

    get renderErroCarregamento() {
        const corErro = red[800];

        return (
            <Box sx={{ p: 1 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                >
                    <ErrorIcon
                        fontSize={'large'}
                        sx={{ fill: corErro }}
                    />
                    <Typography sx={{ ml: 2, color: corErro }}>
                        Falha no carregamento. Tente novamente mais tarde ou contacte a equipe responsável pela
                        aplicação.
                    </Typography>
                </Stack>
                <Paper
                    variant="outlined"
                    sx={{ p: 2, mt: 5 }}
                >
                    <Typography
                        variant="subtitle2"
                        gutterBottom
                    >
                        Detalhes do erro:
                    </Typography>
                    <Typography sx={{ fontStyle: 'italic' }}>{this.erroConsultaTranscricao}</Typography>
                </Paper>
            </Box>
        );
    }

    get renderInfo() {
        if (!this.dataInclusao && !this.dataAtualizacao) {
            return null;
        }

        const tamanhoFonte = 12;
        const cor = grey[400];
        const estiloTitulo = { color: cor, fontSize: tamanhoFonte };
        const estiloDado = { color: cor, fontSize: tamanhoFonte, fontStyle: 'italic', ml: 0.5 };

        return (
            <Box sx={{ mt: 2, marginLeft: 'auto' }}>
                <Divider sx={{ mb: 1 }} />
                {this.dataInclusao && (
                    <Tooltip
                        title={
                            <Typography sx={{ fontSize: tamanhoFonte }}>
                                Responsável pelo cadastro:{' '}
                                {this.infoResponsavel(this.cpfInclusao, this.localizacaoResponsavelInclusao)}
                            </Typography>
                        }
                    >
                        <Stack
                            direction="row"
                            justifyContent="flex-end"
                        >
                            <Typography sx={estiloTitulo}>Cadastro das informações:</Typography>
                            <Typography sx={estiloDado}>{DataUtil.formatarData(this.dataInclusao)}</Typography>
                        </Stack>
                    </Tooltip>
                )}
                {this.dataAtualizacao && (
                    <Tooltip
                        title={
                            <Typography sx={{ fontSize: tamanhoFonte }}>
                                Responsável pela atualização:{' '}
                                {this.infoResponsavel(this.cpfAtualizacao, this.localizacaoResponsavelAtualizacao)}
                            </Typography>
                        }
                    >
                        <Stack
                            direction="row"
                            justifyContent="flex-end"
                        >
                            <Typography sx={estiloTitulo}>Atualização das informações:</Typography>
                            <Typography sx={estiloDado}>{DataUtil.formatarData(this.dataAtualizacao)}</Typography>
                        </Stack>
                    </Tooltip>
                )}
            </Box>
        );
    }

    get renderForm() {
        return (
            <>
                <Box>
                    <TextField
                        id="transcricao"
                        label="Transcrição exata do documento"
                        variant="outlined"
                        fullWidth
                        multiline
                        minRows={5}
                        value={this.textoTranscricao}
                        onChange={this.handleChangeTranscricao}
                        error={this.hasError}
                        slotProps={{
                            input: {
                                readOnly: !!this.executandoAcao
                            },
                            inputLabel: {
                                shrink: true
                            }
                        }}
                    />
                    <Box sx={{ mt: 1, mr: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Typography fontSize={'small'}>
                            {`${this.textoTranscricao.length} / ${QTD_MAXIMA_CARACTERES_TRANSCRICAO}`}
                        </Typography>
                    </Box>
                </Box>
                <Box>
                    <TextField
                        id="observacao-transcricao"
                        label="Observação livre"
                        variant="outlined"
                        fullWidth
                        multiline
                        value={this.textoObservacao}
                        onChange={this.handleChangeObservacao}
                        error={PERMITIR_APENAS_OBSERVACAO ? this.hasError : false}
                        slotProps={{
                            input: {
                                readOnly: !!this.executandoAcao
                            },
                            inputLabel: {
                                shrink: true
                            },
                            htmlInput: { maxLength: QTD_MAXIMA_CARACTERES_OBSERVACAO }
                        }}
                    />
                    <Box sx={{ mt: 1, mr: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Typography fontSize={'small'}>
                            {`${this.textoObservacao.length} / ${QTD_MAXIMA_CARACTERES_OBSERVACAO}`}
                        </Typography>
                    </Box>
                </Box>
                {!!this.msgErroForm && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Typography
                            color={red[500]}
                            sx={{ fontSize: 12 }}
                        >
                            {this.msgErroForm}
                        </Typography>
                    </Box>
                )}
                {this.renderInfo}
            </>
        );
    }

    render() {
        let conteudoDialog;

        if (this.erroConsultaTranscricao) {
            conteudoDialog = this.renderErroCarregamento;
        } else if (this.executandoAcao === TIPO_ACAO.CONSULTAR) {
            conteudoDialog = this.renderCarregandoDialog;
        } else {
            conteudoDialog = this.renderForm;
        }

        return (
            <Dialog
                open={this.observableProps.open}
                onClose={this.observableProps.handleCloseDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth={false}
                slotProps={{
                    paper: {
                        sx: {
                            width: '80%',
                            maxWidth: 'none' // Remove o limite de largura padrão
                        }
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <DialogTitle id="alert-dialog-title">Transcrição de arquivo de áudio/vídeo</DialogTitle>
                </Box>
                <DialogContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                    }}
                >
                    {conteudoDialog}
                </DialogContent>
                <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, mb: 2, mx: 2 }}>
                    <Box id="botaoCancelar">
                        <Button
                            color="secondary"
                            onClick={this.observableProps.onClose}
                            variant="outlined"
                            data-testid="closeDialog"
                            endIcon={<CancelIcon />}
                            disabled={!!this.executandoAcao}
                        >
                            {this.hasAlteracao ? 'Fechar' : 'Cancelar'}
                        </Button>
                    </Box>
                    <Box id="idBotoesAcao">
                        <Button
                            color="secondary"
                            variant="contained"
                            disabled={!!this.executandoAcao || !this.modoEdicao}
                            endIcon={<DeleteIcon />}
                            onClick={this.handleClickConfirmacaoExclusao}
                            loading={this.executandoAcao === TIPO_ACAO.EXCLUIR}
                            loadingPosition="end"
                        >
                            Excluir
                        </Button>
                        <Button
                            color="primary"
                            variant="contained"
                            sx={{ ml: 2 }}
                            disabled={
                                !!this.executandoAcao || !this.isCamposObrigatoriosPreenchidos || this.hasAlteracao
                            }
                            endIcon={this.modoEdicao ? <SaveIcon /> : <AddCircleIcon />}
                            onClick={this.handleClickSalvar}
                            loading={this.executandoAcao === TIPO_ACAO.SALVAR}
                            loadingPosition="end"
                        >
                            {this.modoEdicao ? 'Salvar' : 'Incluir'}
                        </Button>
                        <ModalConfirmacao
                            open={this.openConfirmacaoExclusao}
                            titulo="Confirmação de exclusão da transcrição"
                            tipoConfirmacao={TIPO_CONFIRMACAO.EXCLUSAO}
                            callbackCancelar={this.handleCloseConfirmacaoExclusao}
                            callbackConfirmar={this.handleClickExcluir}
                        >
                            <Stack direction="row">
                                <Warning sx={{ fill: 'darkorange' }} />
                                <DialogContentText
                                    sx={{ ml: 2 }}
                                    id="alert-dialog-description-exclusao-transcricao"
                                >
                                    Atenção: Essa ação não poderá ser desfeita. Os textos não poderão ser recuperados.
                                    <br />
                                    <br />
                                    Deseja confirmar a exclusão?
                                </DialogContentText>
                            </Stack>
                        </ModalConfirmacao>
                    </Box>
                </DialogActions>
            </Dialog>
        );
    }
}

export default inject('stores')(observer(TranscricaoDocumentoSustentacaoOralDialog));
